import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'

import { ColumnDef, ColumnSort, SortingState } from '@tanstack/react-table'
import markLeadAsViewedByDealer from 'api/leads/markLeadAsViewedByDealer'
import AdaptivePagination from 'components/AdaptivePagination'
import EmptyCard from 'components/Empty/EmptyCard'
import ErrorCard from 'components/error/ErrorCard'
import LoadingSpinner from 'components/LoadingSpinner'
import Table from 'components/Table'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import useUser from 'hooks/useUser'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import useLeadsPageContext from 'pages/LeadsPage/LeadsPageContext'
import LeadActivity from 'pages/LeadsPage/models/lead_activity'
import { LeadDetail, LeadResult } from 'pages/LeadsPage/types'
import LoadingStatus from 'state/enums/LoadingStatus'
import { pushLeadPagination, pushSortingClick } from 'utils/gtm-utils'
import { hasRegionalOrAdminAccess } from 'utils/userAccess'

import { parseLead } from '../../utils'
import getLeadsList from '../../utils/getLeadsList'
import { Activity, Decision, DealerAssigned } from './Columns'
import CtaButtons, { CtaActions } from './CtaButtons'
import { PaginationContainer, StyledCard } from './LeadsSearchTable.styled'

export interface LeadsSearchTableProps {
    cbCtaClicked: (action: CtaActions, leadId: number) => void
    hasRiskTierColumn?: boolean
}

export type LeadsSearchTableRef = {
    markLeadAsViewed: (viewedLead: LeadDetail) => Promise<void>
}
export interface TableMetaProps {
    cbCtaClicked: (action: CtaActions, leadId: number) => void
}

export type SortingParamOrder = 'asc' | 'desc'
export interface SortingParam {
    field: string
    order: SortingParamOrder
}

export type SortingParams = Array<SortingParam>

const defaultSortingParam: SortingParam = {
    field: 'received_n_days_ago',
    order: 'asc',
}

export function reorderByLastChanged(
    prevSortingState: SortingState | undefined,
    sortingState: SortingState,
): SortingState {
    if (!prevSortingState) return sortingState

    const lastChangedId = sortingState.find((current) => {
        const previous = prevSortingState.find((prev) => prev.id === current.id)

        return !previous || previous.desc !== current.desc
    })?.id

    if (!lastChangedId) return sortingState

    const lastChanged = sortingState.find(
        (value) => value.id === lastChangedId,
    ) as ColumnSort

    return [
        lastChanged,
        ...sortingState.filter((value) => value.id !== lastChangedId),
    ]
}

function parseSortingStateToSortingParams(
    sortingState: SortingState,
): SortingParams {
    const sortingParams = sortingState.map<SortingParam>(
        (value: ColumnSort) => ({
            field: value.id,
            order: !value.desc ? 'asc' : 'desc',
        }),
    )

    if (!sortingParams.find((el) => el.field === defaultSortingParam.field)) {
        sortingParams.push(defaultSortingParam)
    }

    return sortingParams
}

const ACTIVITY: ColumnDef<LeadResult> = {
    header: 'Activity',
    accessorKey: 'lastActivity',
    cell: ({ getValue }) => {
        const lastActivity = getValue<LeadActivity | null>()

        return lastActivity ? <Activity value={lastActivity} /> : null
    },
    size: 100,
    enableSorting: false,
}

const RECEIVED: ColumnDef<LeadResult> = {
    header: 'Received',
    id: 'received_n_days_ago',
    accessorKey: 'received',
    size: 150,
}

const DECISION: ColumnDef<LeadResult> = {
    id: 'status',
    header: 'Decision',
    accessorKey: 'decision',
    cell: ({ getValue }) => <Decision value={getValue() as string} />,
    size: 200,
    enableSorting: false,
}

const SOURCE: ColumnDef<LeadResult> = {
    header: 'Source',
    accessorKey: 'source',
    size: 200,
    enableSorting: false,
}

const NAME: ColumnDef<LeadResult> = {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    size: 250,
    cell: ({ getValue }) => (
        <span data-dd-privacy="mask">{getValue() as string}</span>
    ),
}

const ASSIGNED: ColumnDef<LeadResult> = {
    id: 'assigned',
    header: 'Assigned to',
    accessorKey: 'assigned',
    cell: ({ getValue, row }) => {
        const assignedValue = getValue() as { application_root_id?: number }

        const value = assignedValue?.application_root_id
            ? assignedValue
            : row.original.id

        return <DealerAssigned value={value} />
    },
    size: 250,
    enableSorting: false,
}

const DEALERSHIP: ColumnDef<LeadResult> = {
    id: 'dealership',
    header: 'Dealership',
    accessorKey: 'dealership',
    cell: ({ getValue }) => {
        const dealership = getValue<{ id: number; name: string } | undefined>()

        return dealership ? dealership.name : '-'
    },
    size: 200,
    enableSorting: true,
}

const CONDITION: ColumnDef<LeadResult> = {
    header: 'Condition',
    accessorKey: 'condition',
    size: 100,
    enableSorting: false,
}

export const RISK_TIER: ColumnDef<LeadResult> = {
    id: 'risk_tier',
    header: 'Risk Tier',
    accessorKey: 'bestRiskTierLabel',
    cell: ({ getValue }) => (
        <span style={{ whiteSpace: 'nowrap' }}>
            {getValue<string | null>() ?? 'N/A'}
        </span>
    ),
    size: 100,
    // Sorting is server-side and bestRiskTierLabel can be null, which makes
    // tanstack's auto direction detection (based on the first row's value
    // type) flip between renders and break the asc/desc toggle cycle.
    sortDescFirst: false,
}

const VEHICLE_REQUESTED: ColumnDef<LeadResult> = {
    header: 'Vehicle Requested',
    accessorKey: 'vehicleRequested',
    size: 400,
    enableSorting: false,
}

const ACTION_BUTTONS: ColumnDef<LeadResult> = {
    accessorKey: 'id',
    id: 'actions',
    cell: ({ row, table }) => {
        const lead = row.original
        const { cbCtaClicked } = table.options.meta as TableMetaProps

        return <CtaButtons lead={lead} cbCtaClicked={cbCtaClicked} />
    },
    size: 400,
    enableSorting: false,
}

const buildLeadColumns = (
    showDealershipColumn: boolean,
    showRiskTierColumn: boolean,
) => {
    const baseColumns = [ACTIVITY, RECEIVED, DECISION, SOURCE, NAME]
    const riskTierColumns = showRiskTierColumn ? [RISK_TIER] : []

    const afterNameColumns = [
        ...riskTierColumns,
        CONDITION,
        VEHICLE_REQUESTED,
        ACTION_BUTTONS,
    ]

    if (showDealershipColumn) {
        return [...baseColumns, DEALERSHIP, ...afterNameColumns]
    }

    return [...baseColumns, ...afterNameColumns]
}

const buildFlexLinkColumns = (
    showDealershipColumn: boolean,
    showRiskTierColumn: boolean,
) => {
    const baseColumns = [ACTIVITY, RECEIVED, DECISION, SOURCE, NAME, ASSIGNED]
    const riskTierColumns = showRiskTierColumn ? [RISK_TIER] : []

    const afterAssignedColumns = [
        ...riskTierColumns,
        CONDITION,
        VEHICLE_REQUESTED,
        ACTION_BUTTONS,
    ]

    if (showDealershipColumn) {
        return [...baseColumns, DEALERSHIP, ...afterAssignedColumns]
    }

    return [...baseColumns, ...afterAssignedColumns]
}

function usePrevious<T>(value: T) {
    const ref = useRef<T>()

    useEffect(() => {
        ref.current = value
    }, [value])

    return ref.current
}

function LeadsSearchTable(
    { cbCtaClicked, hasRiskTierColumn = false }: LeadsSearchTableProps,
    ref?: React.Ref<LeadsSearchTableRef>,
): JSX.Element {
    const {
        applicationFilters: {
            prequalifiedLeads: prequalifiedChecked,
            declinedLeads: declinedChecked,
            preApprovedLeads: preApprovedChecked,
            dealershipIds,
        },
        loadingStatus,
        setLoadingStatus,
        search,
        leadsSearchResults,
        setLeadsSearchResults,
    } = useLeadsPageContext()

    const { user } = useUser()

    const shouldShowDealershipColumn = hasRegionalOrAdminAccess(
        user?.applicationAccess,
    )

    const pageSize = 20
    const [page, setPage] = useState(1)
    const prevPage = usePrevious(page)
    const [pageCount, setPageCount] = useState(0)
    const [hasNextPage, setHasNextPage] = useState(false)
    const blocksEffectsOnMount = useRef<boolean>(true)
    const prevSortingStateRef = useRef<SortingState | undefined>(undefined)
    const featureFlag = useFeatureFlagContext()

    const [sortingParams, setSortingParams] = useState<
        Array<{
            field: string
            order: string
        }>
    >([])

    const [, setError] = useState<Error | null>(null)

    const markLeadAsViewed = async (viewedLead: LeadDetail): Promise<void> => {
        if (
            viewedLead.lastActivity === null ||
            viewedLead.lastActivity?.type !== 'new'
        )
            return

        const markAsViewedResponse = await markLeadAsViewedByDealer(
            viewedLead.id,
        )

        // If the leadsSearchResults are empty, there is nothing to upate
        if (leadsSearchResults.length === 0) return

        const viewedleadIndex = leadsSearchResults.findIndex(
            (lead: LeadResult) => viewedLead.id === lead.id,
        )

        setLeadsSearchResults([
            ...leadsSearchResults.slice(0, viewedleadIndex),
            {
                ...leadsSearchResults[viewedleadIndex],
                lastActivity:
                    LeadActivity.parseLeadActivityFromApiResponse(
                        markAsViewedResponse,
                    ),
            },
            ...leadsSearchResults.slice(viewedleadIndex + 1),
        ])
    }

    useImperativeHandle(ref, () => ({
        markLeadAsViewed,
    }))

    const cbRefreshTableData = ({ sorting }: { sorting: SortingState }) => {
        const reorderedSorting = reorderByLastChanged(
            prevSortingStateRef.current,
            sorting,
        )

        prevSortingStateRef.current = sorting

        setSortingParams(parseSortingStateToSortingParams(reorderedSorting))
        setPage(1)
    }

    const handleFilter = (
        prequalified: boolean,
        declined: boolean,
        preApproved: boolean,
    ) => {
        const filterParams: string[] = []

        if (prequalified) filterParams.push(`Approved`)

        if (declined) filterParams.push(`Declined`)

        if (preApproved) filterParams.push(`Pre-Approved`)

        return filterParams
    }

    function getLeads() {
        setLoadingStatus(LoadingStatus.LOADING)
        OctaneLogger.debug('Starting to fetch leads')

        getLeadsList(
            page,
            handleFilter(
                prequalifiedChecked,
                declinedChecked,
                preApprovedChecked,
            ),
            sortingParams,
            search,
            dealershipIds,
        )
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((data: any) => {
                const { results, count } = data
                const parsedLeads = results.map(parseLead)

                // Handle optional count field from API
                if (typeof count === 'number') {
                    // Legacy mode: API included count
                    setPageCount(Math.ceil(count / pageSize))
                } else {
                    // New mode: API omitted count
                    setPageCount(0)
                }

                // Always calculate hasNextPage for new mode
                setHasNextPage(results.length === pageSize)

                setLeadsSearchResults(parsedLeads)

                if (results.length !== parsedLeads.length) {
                    OctaneLogger.error(
                        `Parsed leads length of ${parsedLeads.length} does
                        not match expected length of ${results.length}`,
                    )
                }

                setLoadingStatus(LoadingStatus.LOADED)
                setError(null)
            })
            .catch((e: Error) => {
                if (prevPage && prevPage !== page) {
                    setPage(prevPage)
                }

                setError(e)

                OctaneLogger.error(
                    'Error while requesting LeadsList:',
                    undefined,
                    e,
                )

                setLoadingStatus(LoadingStatus.FAILED)
            })
    }

    useEffect(() => {
        if (sortingParams.length > 0 && blocksEffectsOnMount.current) {
            blocksEffectsOnMount.current = false
        }
    }, [sortingParams])

    useEffect(() => {
        if (
            blocksEffectsOnMount.current ||
            loadingStatus === LoadingStatus.LOADING
        )
            return

        OctaneLogger.debug('Fetching leads...')
        setLoadingStatus(LoadingStatus.LOADING)

        getLeads()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        page,
        prequalifiedChecked,
        declinedChecked,
        preApprovedChecked,
        dealershipIds,
        sortingParams,
    ])

    useEffect(() => {
        if (blocksEffectsOnMount.current) return

        setPage(1)
    }, [prequalifiedChecked, declinedChecked, dealershipIds, search])

    useEffect(() => {
        if (blocksEffectsOnMount.current) {
            return () => {}
        }

        const timer = setTimeout(getLeads, 500)

        return () => {
            clearTimeout(timer)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search])

    if (loadingStatus === LoadingStatus.FAILED) {
        OctaneLogger.error('Error while fetching leads.')

        return <ErrorCard />
    }

    return (
        <>
            <StyledCard>
                <Table<TableMetaProps>
                    columns={
                        (!featureFlag?.salesPersonFlexLink
                            ? buildLeadColumns(
                                  shouldShowDealershipColumn,
                                  hasRiskTierColumn,
                              )
                            : buildFlexLinkColumns(
                                  shouldShowDealershipColumn,
                                  hasRiskTierColumn,
                              )) as ColumnDef<any, unknown>[] // eslint-disable-line @typescript-eslint/no-explicit-any
                    }
                    defaultSortingState={[
                        {
                            id: defaultSortingParam.field,
                            desc: defaultSortingParam.order === 'desc',
                        },
                    ]}
                    data={leadsSearchResults}
                    cbRefreshData={cbRefreshTableData}
                    cbCtaClicked={cbCtaClicked}
                    onColumnClick={(column: string) => {
                        pushSortingClick(column)
                    }}
                    disableClientSideSorting
                    enableMultiColumnSorting
                />
                {LoadingStatus.LOADING === loadingStatus && <LoadingSpinner />}
                {LoadingStatus.LOADED === loadingStatus &&
                    leadsSearchResults.length === 0 && <EmptyCard />}
            </StyledCard>
            {(pageCount > 0 || page > 1 || hasNextPage) && (
                <PaginationContainer>
                    <AdaptivePagination
                        currentPage={page}
                        pageCount={pageCount > 0 ? pageCount : undefined}
                        hasNextPage={hasNextPage}
                        disabled={loadingStatus === LoadingStatus.LOADING}
                        onChangePage={(newPage) => {
                            if (newPage < 1 || newPage === page) return

                            // Only validate upper bound if we know the total
                            if (pageCount > 0 && newPage > pageCount) return

                            setPage(newPage)

                            if (newPage > page) pushLeadPagination('Next', page)

                            if (newPage < page)
                                pushLeadPagination('Previous', page)
                        }}
                    />
                </PaginationContainer>
            )}
        </>
    )
}

export default forwardRef<LeadsSearchTableRef, LeadsSearchTableProps>(
    LeadsSearchTable,
)
